import { useQueries } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';

import { api } from '../../../core/network/api';
import AgentFace from '../../../common/components/agent-face/agent-face';
import TierChip from '../../../common/components/tier-chip/tier-chip';
import { IconRepositories, IconUsers } from '../../../common/icons';
import { Gate, useAuth } from '../../../global/contexts/auth/auth';
import { useAuthorNames, useRepositoryInfo, useRepositoryName, useTierLabel } from '../../../global/contexts/lookups/lookups';
import KindBadge from '../../repositories/layouts/kind-badge';
import ProjectFilter from '../../projects/layouts/project-filter';
import RepositoryFilter from '../../repositories/layouts/repository-filter';
import { taskKeys } from '../queries/keys';
import { ALL_MODALITIES, taskPaths } from '../queries/paths';
import { TASK_KIND_OPTIONS, WORKFLOW_STATE_LABEL } from '../utils/form';
import { UNAUTHORIZED, authorizationOf, canBeAuthorized } from '../utils/authorization';
import { isFinding, splitFindings, visibleCount } from '../utils/findings';
import { isOneOff } from '../utils/modality';
import Toggle from '../../../common/components/toggle/toggle';
import AuthorizationBadge from './authorization-badge';
import AuthorizeTask from '../../breakdown/layouts/authorize-task';

import TierFilter from '../../tiers/layouts/tier-filter';
import { boardLink } from '../utils/board-navigation';

import '../styles/board.css';

// The work of a project in four columns, filtered by project.
//
// NOT DRAGGABLE, and that is not a missing feature: `workflow_state` is not in
// the PATCH body. A task moves by being claimed, delivered or reviewed, and
// each of those is its own route with its own rules — the atomic reservation,
// the delivery with its commits, the gate that refuses whoever delivered.
// Dragging a card would have to write a state the backend does not accept from
// a form, so there is nothing to drag. What CAN be done from the panel is the
// review, and it has its own screen; the footer points at it.
//
// EACH COLUMN ASKS FOR ITS OWN STATES. One request per state, in parallel:
// the API filters `workflow_state` by equality, so "pendiente" — which is two
// states — cannot be a single query. What that buys is exact counts: every
// answer carries its own `pagination.total`, so a column can show 8 cards and
// still say there are 63.

// `in_review` GETS ITS OWN COLUMN. Folded into "En progreso" it read as work
// still being done, when it is the opposite: nobody is touching it and it is
// waiting on a person. That is the column that stalls — a delivery sat here
// for four tries because there was no way to review it from the browser — and
// a state that stalls has to be countable at a glance.
const COLUMNS = [
    { key: 'pending', title: 'Pendiente',   states: ['proposed', 'ready'] },
    { key: 'claimed', title: 'En progreso', states: ['claimed'] },
    { key: 'review', title: 'En revisión', states: ['in_review'] },
    { key: 'done', title: 'Hecho',       states: ['done'] },
];

// The states no column claims. They are NOT dropped silently: a blocked task
// that vanished from the board is the one nobody remembers to unblock.
const ASIDE_STATES = ['blocked', 'returned', 'discarded'];

const COLUMN_STATES = COLUMNS.flatMap(column => column.states);
const ALL_STATES = [...COLUMN_STATES, ...ASIDE_STATES];

const KIND_LABEL = Object.fromEntries(
    TASK_KIND_OPTIONS.map(option => [option.value, option.label]),
);

// Each request respects the backend page cap.
const COLUMN_FETCH = 100;

// "Pendiente" holds two different waits: a PROPOSAL waits for a person to
// authorize it, a READY task waits for an agent to take it. The filter narrows
// the column to one of them; the choice lives in `?pendiente=` so it survives a
// reload and can be linked.
const PENDING_FILTERS = [
    { key: '',          label: 'Todas',      states: ['proposed', 'ready'] },
    { key: 'propuestas', label: 'Propuestas', states: ['proposed'] },
    { key: 'listas',    label: 'Listas',     states: ['ready'] },
];

// The API orders by priority, not ID. Read subsequent pages before sorting so
// older high-priority rows do not hide the newest completed tasks.
async function readState(query, signal) {
    const items = [];
    let total;
    do {
        const { data } = await api.get(taskPaths.list(), {
            params: { ...query, offset: items.length }, signal,
        });
        const page = data.items ?? [];
        items.push(...page);
        total = data.pagination?.total ?? items.length;
        if (!page.length) break;
    } while (items.length < total);
    return { items: [...new Map(items.map(task => [task.id, task])).values()], pagination: { total } };
}

export default function TasksBoard() {
    const [params, setParams] = useSearchParams();
    const projectId = params.get('project') || '';
    const repositoryId = params.get('repo') || '';
    const tierId = projectId ? params.get('tier') || '' : '';
    // Existing shared URLs keep their general order until a panel overrides it.
    const panelOrder = key => {
        const value = params.get(`orden_${key}`) || params.get('orden');
        return ['asc', 'desc'].includes(value) ? value : key === 'done' ? 'desc' : 'asc';
    };
    const sortTasks = (items, key) => [...items].sort((a, b) =>
        (panelOrder(key) === 'desc' ? -1 : 1) * (a.id - b.id));
    const link = (path, extra) => boardLink(path, params, extra);

    const tierLabel = useTierLabel();
    const repositoryName = useRepositoryName();
    const repositoryInfo = useRepositoryInfo();
    const { tokenName, tokenKind, userName } = useAuthorNames();
    const { can } = useAuth();
    // TSS vite/05 — can() outside JSX. Each destination is guarded by the
    // permission the backend enforces on it: a tier's and a repository's screens
    // are their edit forms, and the token list sits behind :admin. Without the
    // permission the chip is text, not a link that lands on /unauthorized.
    // Read, not write: the tier link opens its read view.
    const canOpenTiers = can('tiers:read');
    const canOpenRepositories = can('repositories:write');
    const canSeeAgents = can('tokens:admin');

    const results = useQueries({
        queries: ALL_STATES.map(state => {
            const query = { limit: COLUMN_FETCH, offset: 0, workflow_state: state, modality: ALL_MODALITIES };
            if (projectId) query.project_id = projectId;
            if (repositoryId) query.repository_id = repositoryId;
            if (tierId) query.tier_id = tierId;
            return {
                queryKey: taskKeys.list({ ...query, board: 'all-pages' }),
                queryFn: ({ signal }) => readState(query, signal),
            };
        }),
    });

    // The clock a reservation is judged against: when the board asked, not when
    // React last painted. `Date.now()` in render is an impure read, and it would
    // flip a card from held to expired on an unrelated repaint.
    const countedAt = Math.max(0, ...results.map(result => result.dataUpdatedAt || 0));

    const byState = Object.fromEntries(ALL_STATES.map((state, index) => [state, {
        items: results[index].data?.items ?? [],
        total: results[index].data?.pagination?.total ?? 0,
        loading: results[index].isLoading,
    }]));

    const asideTotal = ['returned', 'discarded'].reduce((sum, state) => sum + byState[state].total, 0);
    const loading = results.some(result => result.isLoading);
    const failed = results.find(result => result.error);

    const setProject = (value) => {
        const target = new URLSearchParams(params);
        if (value) target.set('project', value);
        else target.delete('project');
        // A repository belongs to one project: keeping it would empty the board.
        target.delete('repo');
        target.delete('tier');
        setParams(target);
    };

    const setRepository = (value) => {
        const target = new URLSearchParams(params);
        if (value) target.set('repo', value);
        else target.delete('repo');
        setParams(target);
    };

    const setFilter = (key, value) => {
        const target = new URLSearchParams(params);
        if (value) target.set(key, value);
        else target.delete(key);
        setParams(target);
    };

    const pending = PENDING_FILTERS.find(item => item.key === (params.get('pendiente') || ''))
        ?? PENDING_FILTERS[0];

    // FINDINGS ARE HIDDEN BY DEFAULT. Once authorized they are ordinary work in
    // the queue, and a board full of «this other thing is also broken» buries
    // the tasks that were planned. They have their own tab; here they appear on
    // request, and the choice lives in `?hallazgos=mostrar`.
    const showFindings = params.get('hallazgos') === 'mostrar';

    const setShowFindings = (on) => {
        const target = new URLSearchParams(params);
        if (on) target.set('hallazgos', 'mostrar');
        else target.delete('hallazgos');
        setParams(target, { replace: true });
    };

    // How many a state shows once its findings are set aside — exact when the
    // state was fetched whole, the server's total otherwise.
    const shownIn = (state) => {
        const { hidden } = splitFindings(byState[state].items, showFindings);
        return visibleCount({ total: byState[state].total, loaded: byState[state].items.length, hidden });
    };

    const setPending = (key) => {
        const target = new URLSearchParams(params);
        if (key) target.set('pendiente', key);
        else target.delete('pendiente');
        setParams(target, { replace: true });
    };

    if (failed) {
        return (
            <p className="tasks__error">
                {failed.error.message || 'No se pudieron cargar las tareas'}
            </p>
        );
    }

    return (
        <section className="board">
            <header className="board__header">
                <div className="board__heading">
                    <span className="board__hint">Tablero</span>
                    <h2 className="board__title">Trabajo</h2>
                </div>

                {/* El cambio a lista lo manda la pestaña de Desarrollo, que
                    es la que sabe que son dos vistas de la misma consulta. */}
                <div className="board__tools">
                    <ProjectFilter value={projectId} onChange={setProject} />
                    <RepositoryFilter value={repositoryId} projectId={projectId} onChange={setRepository} />
                    <Gate domain="tiers:read"><TierFilter projectId={projectId} value={tierId} onChange={value => setFilter('tier', value)} /></Gate>
                    <Toggle
                        checked={showFindings}
                        onChange={setShowFindings}
                        label="Mostrar hallazgos"
                    />
                </div>
            </header>

            {byState.blocked.total > 0 && (
                <section className="board__blocked" aria-label="Tareas bloqueadas">
                    <header className="column__header">
                        <h3 className="column__title">Bloqueadas</h3>
                        <span className="column__count">{byState.blocked.total}</span>
                        <Link className="board__link" to={link('/admin/development/tareas', { view: 'list', state: 'blocked' })}>Ver en lista</Link>
                    </header>
                    <PanelOrder label="Bloqueadas" value={panelOrder('blocked')} onChange={value => setFilter('orden_blocked', value)} />
                    <ul className="board__blocked-cards">
                        {sortTasks(byState.blocked.items, 'blocked').map(task => (
                            <li className="card" key={task.id}>
                                <span className="card__id">#{task.id}</span>
                                <Link className="card__title" to={link(`/admin/tasks/${task.id}`)}>{task.title}</Link>
                                <span className="card__state card__state--blocked">Bloqueada{isFinding(task) ? ' · Hallazgo' : ''}</span>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            <div className="board__columns">
                {COLUMNS.map(column => {
                    // "Pendiente" joins two states: the order is kept across both,
                    // and its filter narrows it to one of them.
                    const isPending = column.states.includes('proposed');
                    const states = isPending ? pending.states : column.states;
                    const all = sortTasks(states.flatMap(state => byState[state].items), column.key);
                    const { visible: items, hidden } = splitFindings(all, showFindings);
                    const total = states.reduce((sum, s) => sum + byState[s].total, 0);
                    const shown = states.reduce((sum, s) => sum + shownIn(s).count, 0);
                    const exact = states.every(s => shownIn(s).exact);

                    return (
                        <div className="column" key={column.title}>
                            <header className="column__header">
                                <span className="column__title">{column.title}</span>
                                <span
                                    className="column__count"
                                    title={exact ? undefined : 'Puede incluir hallazgos fuera de las tarjetas cargadas'}
                                >
                                    {shown}{exact ? '' : '*'}
                                </span>
                            </header>

                            <PanelOrder label={column.title} value={panelOrder(column.key)} onChange={value => setFilter(`orden_${column.key}`, value)} />

                            {isPending && (
                                <div className="column__filter" role="group" aria-label="Filtrar pendientes">
                                    {PENDING_FILTERS.map(item => (
                                        <button
                                            key={item.key || 'todas'}
                                            type="button"
                                            className={`column__filter-option${pending.key === item.key ? ' column__filter-option--on' : ''}`}
                                            onClick={() => setPending(item.key)}
                                            aria-pressed={pending.key === item.key}
                                        >
                                            {item.label}
                                            {item.states.length === 1 && (
                                                <span className="column__filter-count">
                                                    {shownIn(item.states[0]).count}
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {loading && <p className="column__empty">Cargando...</p>}

                            {!loading && !items.length && (
                                <p className="column__empty">Nada acá.</p>
                            )}

                            <ul className="column__cards">
                                {/* The card is a container and not one big link:
                                    it carries two destinations now — the task and
                                    its tier — and a link inside a link is not
                                    valid HTML. The title is the one that opens the
                                    task. */}
                                {items.map(task => {
                                    const tier = tierLabel(task.tier_id);
                                    // UNA TARJETA EN REVISIÓN ABRE LA REVISIÓN, no la
                                    // ficha de la tarea. Lo que alguien va a hacer con
                                    // una entrega que espera es mirarla y decidirla, y
                                    // eso vive en la cola: la ficha obligaba a volver,
                                    // cambiar de pestaña y buscarla de nuevo.
                                    //
                                    // El proyecto viaja con el enlace para que la cola
                                    // abra con el mismo filtro que el tablero.
                                    const inReview = task.workflow_state === 'in_review';
                                    const to = inReview
                                        ? link('/admin/development/revision', { tarea: task.id })
                                        : link(`/admin/tasks/${task.id}`);

                                    return (
                                        // An unauthorized card steps back: it sits in
                                        // "Pendiente" next to work the queue can hand
                                        // out, and must not read as the same thing.
                                        <li
                                            key={task.id}
                                            className={`card${authorizationOf(task.workflow_state) === UNAUTHORIZED ? ' card--unauthorized' : ''}`}
                                        >
                                            <span className="card__id">#{task.id}</span>
                                            <Link
                                                to={to}
                                                className="card__title"
                                                title={inReview ? 'Abrir su revisión' : undefined}
                                            >
                                                {task.title}
                                            </Link>
                                            {/* Only drawn when findings are shown: says
                                                what it is and where it came from. */}
                                            {isFinding(task) && (
                                                <Link
                                                    to={link(`/admin/tasks/${task.origin_task_id}`)}
                                                    className="card__finding"
                                                    title="Hallazgo: salió de otra tarea"
                                                >
                                                    Hallazgo · de #{task.origin_task_id}
                                                </Link>
                                            )}
                                            <span className="card__meta">
                                                <span className={`card__kind card__kind--${task.kind}`}>
                                                    {KIND_LABEL[task.kind] ?? task.kind}
                                                </span>
                                                {/* The real state, inside its column: a card in
                                                    "Pendiente" is not the same if it is proposed
                                                    or already ready to take. */}
                                                <span className={`card__state card__state--${task.workflow_state}`}>
                                                    {WORKFLOW_STATE_LABEL[task.workflow_state]}
                                                </span>
                                                <AuthorizationBadge workflowState={task.workflow_state} compact />
                                                {/* Right next to «No autorizada»: the
                                                    fix sits beside the state. */}
                                                {canBeAuthorized(task) && (
                                                    <Gate domain="tasks:authorize">
                                                        <AuthorizeTask
                                                            task={task}
                                                            tierId={task.tier_id}
                                                            iconOnly
                                                            className="card__authorize"
                                                        />
                                                    </Gate>
                                                )}
                                                {/* Where the tier would be: a one-off
                                                    task has none, and says so. */}
                                                {isOneOff(task)
                                                    ? <span className="card__oneoff" title="Tarea puntual: sin tier ni plan">Puntual</span>
                                                    : (
                                                        <TierChip
                                                            label={tier?.short}
                                                            title={tier?.full}
                                                            to={canOpenTiers ? `/admin/tiers/${task.tier_id}` : null}
                                                        />
                                                    )}
                                            </span>

                                            {/* Where it lives and who has it, on a
                                                second row: five chips on one line
                                                stop being readable at the width of
                                                a column. */}
                                            <span className="card__meta card__meta--where">
                                                <RepositoryTag
                                                    kind={repositoryInfo(task.repository_id)?.kind}
                                                    name={repositoryName(task.repository_id)}
                                                    to={canOpenRepositories
                                                        ? `/admin/repositories/${task.repository_id}`
                                                        : null}
                                                />
                                                <AssigneeTag task={task} userName={userName} />
                                                {canSeeAgents && (
                                                    <AgentTag
                                                        task={task}
                                                        countedAt={countedAt}
                                                        tokenName={tokenName}
                                                        tokenKind={tokenKind}
                                                        userName={userName}
                                                    />
                                                )}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>

                            {/* Says when the column is showing less than it counts. */}
                            {total > all.length && (
                                <p className="column__more">
                                    Se muestran {all.length} de {total}. Actualiza el tablero para consultar los cambios recientes.
                                </p>
                            )}

                            {hidden > 0 && (
                                <button
                                    type="button"
                                    className="column__hidden"
                                    onClick={() => setShowFindings(true)}
                                >
                                    {hidden} {hidden === 1 ? 'hallazgo oculto' : 'hallazgos ocultos'} · mostrar
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            <footer className="board__foot">
                <p className="board__note">
                    El tablero se mira, no se arrastra: una tarea cambia de columna al
                    tomarla, entregarla o revisarla, y cada una de esas tiene su ruta y sus
                    reglas.{' '}
                    <Link to={link('/admin/development/revision')} className="board__link">
                        Lo que está en revisión se acepta o se devuelve acá
                    </Link>.
                </p>

                {asideTotal > 0 && (
                    <p className="board__note">
                        Hay <strong>{asideTotal}</strong> tareas devueltas o descartadas fuera de las columnas.{' '}
                        <Link to={link('/admin/development/tareas', { view: 'list', state: '' })} className="board__link">
                            Verlas en la lista
                        </Link>
                    </p>
                )}

                <Gate domain="tasks:write">
                    <Link
                        to={link('/admin/tasks/new')}
                        className="board__new"
                    >
                        + Nueva tarea
                    </Link>
                </Gate>
            </footer>
        </section>
    );
}

// El repositorio en la tarjeta: su glifo de tipo en vez del icono genérico.
// Con cuatro columnas estrechas, la forma se lee antes que el nombre — y el
// icono de repositorio era el mismo para los cinco tipos, así que no distinguía
// nada. Sin palabra, porque el nombre va al lado.
function RepositoryTag({ name, kind, to }) {
    if (!name) return null;

    const body = (
        <>
            {kind ? <KindBadge kind={kind} label={false} /> : <IconRepositories />}
            <span className="card__tag-text">{name}</span>
        </>
    );

    if (!to) return <span className="card__tag">{body}</span>;
    return <Link to={to} className="card__tag card__tag--link">{body}</Link>;
}

// WHOSE WORK IT IS: the person assigned, next to the repository, with the
// person glyph. It is `assigned_to` — the owner of the work — and not who holds
// the reservation right now, which is the tag after it.
function AssigneeTag({ task, userName }) {
    if (task.assigned_to == null) return null;

    const name = task.assigned_user?.name ?? userName(task.assigned_to);

    return (
        <span className="card__tag card__tag--assignee" title={`Responsable: ${name}`}>
            <IconUsers />
            <span className="card__tag-text">{name}</span>
        </span>
    );
}

// WHO TOOK IT, and the face only claims liveness where liveness is a fact.
//
// THE NAME IS THE PERSON'S, not the token's: a token acts for somebody, and
// "who took this" is answered by that somebody. The agent stays in the face
// (its kind, drawn) and in the tooltip, by name. A task in `claimed` is being held right now, so its reservation either
// stands or has run out — and one that ran out matters, because the next claim
// takes that task without asking while the board still says "Tomada".
//
// In any other state the token is a record of who did it, not somebody at work:
// delivering does not clear the reservation fields, so a lit face on a task in
// review would be an agent that stopped touching it minutes ago.
function AgentTag({ task, countedAt, tokenName, tokenKind, userName }) {
    if (task.claimed_by_token == null) return null;

    const held = task.workflow_state === 'claimed';
    const until = task.claimed_until ? new Date(task.claimed_until).getTime() : null;
    const isExpired = held && until != null && countedAt > 0 && until <= countedAt;
    const state = held ? (isExpired ? 'expired' : 'working') : 'plain';

    const agent = tokenName(task.claimed_by_token);
    const kind = tokenKind(task.claimed_by_token);
    // Falls back to the agent's name only when the reservation names no person.
    const person = task.claimed_by != null ? userName(task.claimed_by) : null;
    const name = person ?? agent;
    const detail = `Tomada por ${name} · agente ${agent}${kind ? ` (${kind})` : ''}`;

    return (
        <span
            className={'card__tag card__tag--agent card__tag--' + state}
            title={isExpired ? `${detail} — la reserva venció, la tarea vuelve a la cola` : detail}
        >
            <AgentFace state={state} kind={kind} size={18} title={agent} />
            <span className="card__tag-text">{name}</span>
        </span>
    );
}

function PanelOrder({ label, value, onChange }) {
    return (
        <label className="column__order">
            <span>Orden por ID</span>
            <select aria-label={`Orden de ${label}`} className="board__filter" value={value} onChange={event => onChange(event.target.value)}>
                <option value="asc">Ascendente ↑</option>
                <option value="desc">Descendente ↓</option>
            </select>
        </label>
    );
}
